import type { Icon as IconType } from '@rover/icons';
import { Flex, Text } from '@rover/kibble/core';
import TextWithSingleInlineColor from '@rover/kibble/official-patterns/TextWithSingleInlineColor/TextWithSingleInlineColor';
import { isTaggedParagraphText } from '@rover/kibble/official-patterns/TextWithSingleInlineLink/TextWithSingleInlineLink.common';
import type { ThemeTextColor } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';

export type Props = {
  text: string;
  icon: IconType;
  iconFill?: string;
  highlightColor?: ThemeTextColor;
  ariaLabel?: string;
  dataTestId?: string;
};

/**
 * @param text The text to render, can optionally include <0></0> tags to get it
 * highlighted with highlightColor
 * @param icon The icon to render to the left of the text
 * @param iconFill Optional color for the icon, defaults to theme.textColor.primary
 * @param highlightColor Optional color for highlighted text, defaults to theme.textColor.primary
 * @param ariaLabel Optional aria-label for accessibility
 * @param dataTestId Optional data-testid for testing purposes
 */
const SitterHighlight: React.FC<Props> = ({
  icon: Icon,
  text,
  iconFill,
  highlightColor = 'primary',
  ariaLabel,
  dataTestId,
}: Props) => {
  const theme = useTheme();
  const textProps = {
    'aria-label': ariaLabel,
    size: '200' as const,
    textColor: 'secondary' as const,
  };
  return (
    <Flex gap="2x" flexDirection="row" data-testid={dataTestId} alignItems="center">
      <Icon height="20px" width="20px" fill={iconFill ?? theme.textColor.primary} />
      {isTaggedParagraphText(text) ? (
        <TextWithSingleInlineColor
          paragraphText={text}
          highlightColor={highlightColor}
          {...textProps}
        />
      ) : (
        <Text {...textProps}>{text}</Text>
      )}
    </Flex>
  );
};

export default SitterHighlight;
